import { useState, useEffect, useCallback } from 'react';
import { UnifiedOnboardingData, UnifiedOnboardingStep, CoachPersonality } from '../lib/onboarding/unifiedTypes';
import { validateUnifiedStep } from '../lib/onboarding/unifiedValidation';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'onewin_unified_onboarding_data';

const initialData: UnifiedOnboardingData = {
  // Step 1: Account Creation
  email: '',
  password: '',
  fullName: '',
  phoneNumber: '',
  countryCode: '+1',

  // Step 2: Coach Personality
  selectedPersonality: null,

  // Step 3: Payment
  paymentCompleted: false,
  customerId: '',
  priceId: '',
  paymentMethodId: '',

  // Step 4: Timing
  morningTime: '08:00',
  eveningTime: '18:00',
  timezone: 'America/New_York (EST)',

  // Step 5: Intensity
  intensityLevel: 3,

  // Step 6: Additional Preferences
  additionalPreferences: '',
  communicationPreference: 'text-and-voice',

  // Step 7: Completion
  completed: false,
};

export function useUnifiedOnboarding() {
  const { user, updateUserPreferences, userPreferences } = useAuth();
  const [data, setData] = useState<UnifiedOnboardingData>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...initialData, ...JSON.parse(stored) } : initialData;
    } catch (error) {
      console.error('Error loading unified onboarding data from localStorage:', error);
      return initialData;
    }
  });

  const [currentStep, setCurrentStep] = useState<UnifiedOnboardingStep>(0);

  // Load existing onboarding data from database when user is authenticated
  useEffect(() => {
    if (user && userPreferences) {
      const databaseOnboardingData: Partial<UnifiedOnboardingData> = {};

      if (userPreferences.coach_personality) {
        databaseOnboardingData.selectedPersonality = userPreferences.coach_personality as CoachPersonality;
      }
      if (userPreferences.intensity_level) {
        databaseOnboardingData.intensityLevel = userPreferences.intensity_level;
      }
      if (userPreferences.additional_preferences) {
        databaseOnboardingData.additionalPreferences = userPreferences.additional_preferences;
      }

      // Only update if we have data from the database
      if (Object.keys(databaseOnboardingData).length > 0) {
        setData(prev => ({ ...prev, ...databaseOnboardingData }));
      }
    }
  }, [user, userPreferences]);

  // Save to localStorage whenever data changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving unified onboarding data to localStorage:', error);
    }
  }, [data]);

  // Save onboarding-specific data to database when user is authenticated and data changes
  useEffect(() => {
    const saveToDatabase = async () => {
      if (!user) return;

      // Only save onboarding-specific fields that are in the user_preferences table
      const onboardingPreferences = {
        coach_personality: data.selectedPersonality,
        intensity_level: data.intensityLevel,
        additional_preferences: data.additionalPreferences,
      };

      // Only update if we have meaningful data to save
      const hasOnboardingData = onboardingPreferences.coach_personality ||
                               onboardingPreferences.intensity_level ||
                               onboardingPreferences.additional_preferences;

      if (hasOnboardingData) {
        try {
          const { error } = await updateUserPreferences(onboardingPreferences);
          if (error) {
            console.error('Error saving onboarding data to database:', error);
          }
        } catch (error) {
          console.error('Error updating user preferences:', error);
        }
      }
    };

    saveToDatabase();
  }, [user, data.selectedPersonality, data.intensityLevel, data.additionalPreferences, updateUserPreferences]);

  const updateData = useCallback((updates: Partial<UnifiedOnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const reset = useCallback(() => {
    setData(initialData);
    setCurrentStep(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing unified onboarding data from localStorage:', error);
    }
  }, []);

  const isStepValid = useCallback((step: UnifiedOnboardingStep): boolean => {
    return validateUnifiedStep(step, data);
  }, [data]);

  const isJudge = useCallback((): boolean => {
    return data.fullName.toLowerCase().includes('judge');
  }, [data]);

  const canGoNext = useCallback((): boolean => {
    return isStepValid(currentStep);
  }, [currentStep, isStepValid]);

  const goToStep = useCallback((step: UnifiedOnboardingStep) => {
    if (step >= 0 && step <= 6) {
      setCurrentStep(step);
    }
  }, []);

  const goNext = useCallback(() => {
    if (canGoNext() && currentStep < 6) {
      setCurrentStep(prev => (prev + 1) as UnifiedOnboardingStep);
    }
  }, [canGoNext, currentStep]);

  const skipNext = useCallback(() => {
    if (canGoNext() && currentStep < 6) {
      setCurrentStep(prev => (prev + 2) as UnifiedOnboardingStep);
    }
  }, [canGoNext, currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => (prev - 1) as UnifiedOnboardingStep);
    }
  }, [currentStep]);

  const clearStorage = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing unified onboarding storage:', error);
    }

    // Also clear onboarding data from database if user is authenticated
    if (user) {
      const clearOnboardingData = async () => {
        try {
          const { error } = await updateUserPreferences({
            coach_personality: null,
            intensity_level: null,
            additional_preferences: null,
          });
          if (error) {
            console.error('Error clearing onboarding data from database:', error);
          }
        } catch (error) {
          console.error('Error clearing onboarding data from database:', error);
        }
      };
      clearOnboardingData();
    }
  }, [user, updateUserPreferences]);

  return {
    data,
    currentStep,
    updateData,
    reset,
    isStepValid,
    isJudge,
    skipNext,
    canGoNext,
    goToStep,
    goNext,
    goBack,
    clearStorage,
  };
}
